import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useLanguageStore } from '@/stores/locale';
import deFlag from '@/assets/images/flags/de.png';
import enFlag from '@/assets/images/flags/en.png';
import frFlag from '@/assets/images/flags/fr.png';
import huFlag from '@/assets/images/flags/hu.png';
import type { SupportedLocale } from '@/plugins/i18n';

export interface LocaleOption {
  locale: SupportedLocale;
  img: string;
  name: string;
}

export const LOCALES: LocaleOption[] = [
  {
    locale: 'de',
    img: deFlag,
    name: 'Deutsch',
  },
  {
    locale: 'en',
    img: enFlag,
    name: 'English',
  },
  {
    locale: 'hu',
    img: huFlag,
    name: 'Magyar',
  },
  {
    locale: 'fr',
    img: frFlag,
    name: 'Français',
  },
];

export function useLanguageSelector() {
  const { locale } = useI18n();
  const languageStore = useLanguageStore();

  const locales = ref<LocaleOption[]>(LOCALES);

  const getLocaleOption = (localeCode: string): LocaleOption => {
    return locales.value.find((l) => l.locale === localeCode) ?? locales.value[0];
  };

  const selectedLocale = ref<LocaleOption>(getLocaleOption(locale.value));

  function selectLanguage(newLocale: SupportedLocale) {
    languageStore.setLanguage(newLocale);
    locale.value = newLocale;
    selectedLocale.value = getLocaleOption(newLocale);
  }

  // Synchronize the selected language with the store
  watch(
    () => languageStore.locale,
    (newLocale) => {
      selectedLocale.value = getLocaleOption(newLocale);
      locale.value = newLocale;
    },
  );

  return {
    locales,
    selectedLocale,
    selectLanguage,
  };
}
